import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { createContext, type ReactNode, useContext } from "react";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import type { DispatchItem, MessageText } from "@/hooks/dispatch/dispatchItems";
import { type DispatchTableRow, rowTask } from "@/hooks/dispatch/dispatchTableRow";
import type { TaskMenuAction } from "@/hooks/dispatch/taskMenu";
import { taskDriverLabel } from "@/hooks/dispatch/taskOptions";
import { type ItemTarget, type TaskFold, taskItemIdOf } from "@/hooks/dispatch/tasks";
import { zinc } from "@/ui/tokens";
import { DISPATCH_TASK, DISPATCH_TASK_PALETTE } from "./dispatchColors";
import { formatRelativeTime } from "./format";

/**
 * What the page's task surfaces read while the workspace shows tasks: the fold, a board's label, a member's name, and
 * the minute clock. Null while tasks are off, and every task surface then renders nothing.
 */
export type DispatchTaskState = {
  fold: TaskFold;
  /** A board's label by view id; null for a view the workspace no longer has. */
  viewLabelFor: (viewId: string) => string | null;
  /** A member's name by membership id; null when the store has none. */
  memberNameFor: (membershipId: string | null) => string | null;
  now: number;
  /** The driver the History panel is open on, whom a task from one of its entries can name instead; null for none. */
  historyDriverId: string | null;
  /** Whether a new task may be about a message or event; off, every new task is about the driver. */
  itemTasks: boolean;
  /** The board the page shows, on which a task pinned there needs no chip naming it. */
  currentViewId: string;
  /** What a task's message or event reads, by its Dispatch item id; undefined for one the store does not hold. */
  subjectTextFor: (itemId: string) => MessageText | undefined;
  /**
   * Runs a task menu entry from History: make opens the picker on the message or event, or on the panel's driver when
   * `target` is null; move opens the picker on the entry's task in Move mode.
   */
  onItemTaskAction: (target: ItemTarget | null, action: TaskMenuAction) => void;
};

/** The page's task state for the cells and History; null while the workspace shows no tasks. */
export const DispatchTaskContext = createContext<DispatchTaskState | null>(null);

/**
 * What names a task's board when it is on no board the workspace has, which shows it on every shelf: on the chip, the
 * line, and History's task cards.
 */
export const EVERY_BOARD = "Every board";

/** The board a task is pinned to, by its label; "Every board" for one with no pin, or a pin to a view now gone. */
function boardOf(open: OpenTask, viewLabelFor: DispatchTaskState["viewLabelFor"]): string {
  return open.pin ? (viewLabelFor(open.pin.viewId) ?? EVERY_BOARD) : EVERY_BOARD;
}

/**
 * The orange chip naming the board a row's open task is pinned to, with its note and its author on hover.
 *
 * Renders nothing for a row with no open task, or while tasks are off, and nothing on the board the task is pinned to,
 * where the chip would only name the page. The red dot keeps its meaning beside it.
 */
function TaskChip({ open, state }: { open: OpenTask; state: DispatchTaskState }) {
  if (open.pin?.viewId === state.currentViewId) return null;
  const board = boardOf(open, state.viewLabelFor);
  const author = state.memberNameFor(open.task.createdBy);
  const made = formatRelativeTime(open.task.createdAt, state.now)?.short ?? "";
  const byline = author ? `${author}, ${made}` : made;
  return (
    <Tooltip title={open.task.note ? `${open.task.note} (${byline})` : byline}>
      <Box
        component="span"
        data-sb={TestIds.dispatch.taskChip}
        sx={{
          flex: "none",
          px: 0.75,
          height: 18,
          lineHeight: "18px",
          borderRadius: 9,
          border: `1px solid ${DISPATCH_TASK}`,
          color: DISPATCH_TASK,
          fontSize: "0.6875rem",
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {board}
      </Box>
    </Tooltip>
  );
}

/**
 * How a row's open task ends its line in the Message cell, after the assigner and the note: "Assigned to <board>".
 *
 * @param open - The row's open task.
 * @param state - The page's task state, for the board's label.
 */
export function taskAssignmentLine(open: OpenTask, state: Pick<DispatchTaskState, "viewLabelFor">): string {
  return `Assigned to ${boardOf(open, state.viewLabelFor)}`;
}

/**
 * What a task is about, as its line names it: the driver by name, or the message or event by its Dispatch item id and
 * text; null for an item the store does not hold.
 */
export type TaskSubjectShown =
  | { kind: "driver"; name: string }
  | { kind: "item"; itemId: string; message: MessageText }
  | null;

/**
 * What a row's open task is about, for its line when it carries no note: the message or event it names, else the
 * driver, by the row's driver's name, which is who a task about a driver shows on. Null for a message the store does
 * not hold, or whose text has not downloaded yet.
 *
 * @param open - The row's open task.
 * @param row - The row it shows on.
 * @param subjectTextFor - What a message or event reads, by its Dispatch item id.
 */
export function taskSubjectShown(
  open: OpenTask,
  row: DispatchTableRow,
  subjectTextFor: DispatchTaskState["subjectTextFor"],
): TaskSubjectShown {
  const itemId = taskItemIdOf(open);
  if (itemId === null) return { kind: "driver", name: taskDriverLabel(row.driver) };
  const own = rowItem(row);
  const message = own?.id === itemId ? own : subjectTextFor(itemId);
  // A message whose text has not arrived yet names nothing, rather than an empty line.
  return message?.text ? { kind: "item", itemId, message } : null;
}

/** The message or event a message row or a task row stands for; null on a driver row and a task about a driver. */
function rowItem(row: DispatchTableRow): DispatchItem | null {
  return row.kind === "driver" ? null : (row.item ?? null);
}

/**
 * A Message cell for a row with an open task: the chip (off the task's own board), then the task's line, "<assigner>:
 * <note> Assigned to <board>". With no note, what the task is about stands in for it: the driver, or the message or
 * event in the cell's own rendering. The row's message, when the line replaces it, shows on hover. The text alone, unwrapped, when
 * the row has no open task.
 */
export function WithTaskChip({
  row,
  original,
  renderMessage,
  children,
}: {
  row: DispatchTableRow;
  /** The row's message in full for the hover; null when the row has no message. */
  original: ReactNode;
  /** Renders a message or event's text as the cell renders its own, for a task about one other than the row's. */
  renderMessage: (message: MessageText) => ReactNode;
  /** The cell's rendering of the row's own message. */
  children: ReactNode;
}) {
  const state = useContext(DispatchTaskContext);
  const open = state ? rowTask(row, state.fold) : null;
  if (!state || !open) return <>{children}</>;
  const note = open.task.note?.trim();
  const subject = note ? null : taskSubjectShown(open, row, state.subjectTextFor);
  // The row's own message is the subject on a message row and on a task row about one, and needs no hover.
  const ownMessage = subject?.kind === "item" && rowItem(row)?.id === subject.itemId;
  const assigner = state.memberNameFor(open.task.createdBy);
  const prefix = assigner ? (
    <Box component="span" sx={{ color: DISPATCH_TASK_PALETTE.text }}>
      {`${assigner}: `}
    </Box>
  ) : null;
  // A note or the driver's name reads as one run of text with the assigner, so one ellipsis cuts it at its right end.
  let lead: ReactNode = null;
  if (note || subject?.kind === "driver") {
    lead = (
      <Ellipsis>
        {prefix}
        {note || (subject?.kind === "driver" ? subject.name : "")}
      </Ellipsis>
    );
  } else if (subject?.kind === "item") {
    lead = (
      <>
        {prefix && <Box sx={{ flex: "none", whiteSpace: "pre" }}>{prefix}</Box>}
        <Box sx={{ minWidth: 0, display: "flex" }}>{ownMessage ? children : renderMessage(subject.message)}</Box>
      </>
    );
  }
  // The line is cut from the right only. The lead never shrinks to make room for the assignment: the assignment gives
  // way first, down to nothing, and only a lead wider than the cell is cut, at its own right end. The lead's cap is the
  // cell less the icon and the gap after it.
  const line = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flex: 1, overflow: "hidden" }}>
      <StickyNote2OutlinedIcon aria-label="Task" sx={{ flex: "none", fontSize: 14, color: DISPATCH_TASK }} />
      {lead && (
        <Box sx={{ flex: "0 0 auto", minWidth: 0, maxWidth: "calc(100% - 20px)", display: "flex", color: zinc[200] }}>
          {lead}
        </Box>
      )}
      <Box
        component="span"
        data-sb={TestIds.dispatch.taskLine}
        sx={{
          flex: "0 1 auto",
          minWidth: 0,
          color: DISPATCH_TASK_PALETTE.text,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {taskAssignmentLine(open, state)}
      </Box>
    </Box>
  );
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <TaskChip open={open} state={state} />
      {original && !ownMessage ? (
        <Tooltip title={original} placement="bottom-start" slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}>
          {line}
        </Tooltip>
      ) : (
        line
      )}
    </Box>
  );
}

/** Text cut to one line with an ellipsis. */
function Ellipsis({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {children}
    </Box>
  );
}
